import React, { useEffect, useRef, useState, useCallback } from 'react';
import rrwebPlayer from 'rrweb-player';
import type { eventWithTime } from 'rrweb';
import 'rrweb-player/dist/style.css';

interface DomRecordingPlayerProps {
  sourceUrl: string;
  audioUrl?: string | null;
  audioSyncThresholdSec?: number;
  className?: string;
  /** When true, the player uses the container width and a taller height allowance. */
  fillContainer?: boolean;
  onPlayerReady?: (api: { goto: (timeInSeconds: number) => void }) => void;
  onDurationChange?: (durationSeconds: number) => void;
}

type DomRecordingPayload =
  | eventWithTime[]
  | { events?: eventWithTime[] }
  | { data?: eventWithTime[] };

const getEventsFromPayload = (payload: DomRecordingPayload): eventWithTime[] => {
  if (Array.isArray(payload)) return payload;
  if (payload?.events && Array.isArray(payload.events)) return payload.events;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  return [];
};

const CONTROLLER_HEIGHT = 80;
const MAX_INITIAL_WIDTH = 960;  // Cap initial player size for proportional controls
const MAX_INITIAL_HEIGHT = 540;

const DomRecordingPlayer: React.FC<DomRecordingPlayerProps> = ({
  sourceUrl,
  audioUrl,
  audioSyncThresholdSec = 0.25,
  className = '',
  fillContainer = false,
  onPlayerReady,
  onDurationChange,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<rrwebPlayer | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const lastAudioSyncRef = useRef(0);
  const [events, setEvents] = useState<eventWithTime[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const recordingMetaRef = useRef<{ width: number; height: number }>({
    width: 1280,
    height: 720,
  });
  const isPlayerReadyRef = useRef(false);
  const initialPlayerSizeRef = useRef<{ width: number; height: number } | null>(null);

  // Calculate optimal player dimensions based on available space
  const calculateDimensions = useCallback(() => {
    const wrapper = wrapperRef.current;
    const { width: recordingWidth, height: recordingHeight } = recordingMetaRef.current;
    const aspect = recordingWidth / recordingHeight;

    const layoutContainer = wrapper?.closest<HTMLElement>('[data-rrweb-container]');

    let availableWidth: number;
    let availableHeight: number;

    if (fillContainer && layoutContainer?.clientWidth) {
      // Use container dimensions directly for tighter fit
      availableWidth = layoutContainer.clientWidth;
      availableHeight = window.innerHeight * 0.72;
    } else {
      // Default: use viewport dimensions as reliable sizing reference
      const maxViewportWidth = window.innerWidth * 0.85;
      availableWidth = layoutContainer?.clientWidth || maxViewportWidth;
      availableHeight = window.innerHeight * 0.55;
      // Cap to reasonable maximums
      availableWidth = Math.min(availableWidth, 1400);
    }

    availableHeight = Math.min(availableHeight, 700);

    // Calculate dimensions maintaining aspect ratio
    const heightForVideo = availableHeight - CONTROLLER_HEIGHT;

    let width = availableWidth;
    let height = width / aspect;

    // If fillContainer, always use full width — let height follow naturally
    // Otherwise, constrain by height if it exceeds available space
    if (!fillContainer && height > heightForVideo) {
      height = heightForVideo;
      width = height * aspect;
    }

    const finalWidth = Math.floor(Math.max(300, width));
    const finalHeight = Math.floor(Math.max(170, height));

    return { width: finalWidth, height: finalHeight };
  }, [fillContainer]);

  // Apply size via CSS transform scaling (preserves playback state)
  const applySize = useCallback((targetWidth: number, targetHeight: number) => {
    const container = containerRef.current;
    const wrapper = wrapperRef.current;
    const initialSize = initialPlayerSizeRef.current;

    if (!container || !wrapper || !initialSize) return;

    // Calculate scale factor based on initial player size (video area only)
    const scaleX = targetWidth / initialSize.width;
    const scaleY = targetHeight / initialSize.height;
    const scale = Math.min(scaleX, scaleY);

    // Calculate actual scaled dimensions (including controller)
    const initialTotalHeight = initialSize.height + CONTROLLER_HEIGHT;
    const scaledWidth = Math.ceil(initialSize.width * scale);
    const scaledHeight = Math.ceil(initialTotalHeight * scale);

    // Set wrapper to exactly match scaled content
    wrapper.style.width = `${scaledWidth}px`;
    wrapper.style.height = `${scaledHeight}px`;
    wrapper.style.overflow = 'hidden';

    // Apply transform to scale the player content
    container.style.transformOrigin = 'top left';
    container.style.transform = `scale(${scale})`;
    container.style.width = `${initialSize.width}px`;
    container.style.height = `${initialTotalHeight}px`;

    // Update shell if it exists
    const shell = wrapper.closest<HTMLElement>('.rrweb-shell');
    if (shell) {
      shell.style.width = `${scaledWidth}px`;
      shell.style.height = `${scaledHeight}px`;
      shell.style.overflow = 'hidden';
    }
  }, []);

  // Resize handler
  const handleResize = useCallback(() => {
    if (!isPlayerReadyRef.current || !initialPlayerSizeRef.current) return;
    const { width, height } = calculateDimensions();
    applySize(width, height);
  }, [calculateDimensions, applySize]);

  // Fetch events
  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);

    fetch(sourceUrl)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Failed to load DOM recording (${response.status})`);
        }
        return response.json() as Promise<DomRecordingPayload>;
      })
      .then((payload) => {
        if (!isMounted) return;
        const loadedEvents = getEventsFromPayload(payload);
        if (!loadedEvents.length) {
          throw new Error('DOM recording is empty');
        }
        // Extract recording dimensions from meta event
        const metaEvent = loadedEvents.find((event) => event.type === 4);
        if (metaEvent?.data?.width && metaEvent?.data?.height) {
          recordingMetaRef.current = {
            width: metaEvent.data.width,
            height: metaEvent.data.height,
          };
        }
        // Calculate and report duration from event timestamps
        if (loadedEvents.length > 1 && onDurationChange) {
          const firstTimestamp = loadedEvents[0].timestamp;
          const lastTimestamp = loadedEvents[loadedEvents.length - 1].timestamp;
          const durationMs = lastTimestamp - firstTimestamp;
          const durationSeconds = Math.ceil(durationMs / 1000);
          if (durationSeconds > 0) {
            onDurationChange(durationSeconds);
          }
        }
        setEvents(loadedEvents);
      })
      .catch((err: unknown) => {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load DOM recording');
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [sourceUrl]);

  // Set up resize listener
  useEffect(() => {
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

    const debouncedResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);

    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [handleResize]);

  // Create player (only when events change)
  useEffect(() => {
    if (!containerRef.current || !wrapperRef.current || !events || !events.length) return;

    isPlayerReadyRef.current = false;
    initialPlayerSizeRef.current = null;

    // Destroy existing player
    if (playerRef.current) {
      if (typeof (playerRef.current as { destroy?: () => void }).destroy === 'function') {
        (playerRef.current as { destroy: () => void }).destroy();
      }
      playerRef.current = null;
    }

    containerRef.current.innerHTML = '';
    containerRef.current.style.transform = '';

    // Calculate target dimensions (what we want to display)
    const { width: targetWidth, height: targetHeight } = calculateDimensions();

    // Cap initial player size for proportional controls on large screens
    // This ensures controls stay reasonably sized even on ultrawides
    const { width: recordingWidth, height: recordingHeight } = recordingMetaRef.current;
    const recordingAspect = recordingWidth / recordingHeight;

    let initialWidth = Math.min(targetWidth, MAX_INITIAL_WIDTH);
    let initialHeight = initialWidth / recordingAspect;

    if (initialHeight > MAX_INITIAL_HEIGHT) {
      initialHeight = MAX_INITIAL_HEIGHT;
      initialWidth = initialHeight * recordingAspect;
    }

    // Store initial size for scaling calculations
    initialPlayerSizeRef.current = { width: initialWidth, height: initialHeight };

    const player = new rrwebPlayer({
      target: containerRef.current,
      props: {
        events,
        autoPlay: false,
        showController: true,
        width: initialWidth,
        height: initialHeight,
        maxScale: 1,
      },
    });

    // Immediately apply scaling to match target dimensions
    const initialTotalHeight = initialHeight + CONTROLLER_HEIGHT;
    const scaleX = targetWidth / initialWidth;
    const scaleY = targetHeight / initialHeight;
    const scale = Math.min(scaleX, scaleY);

    const scaledWidth = Math.ceil(initialWidth * scale);
    const scaledHeight = Math.ceil(initialTotalHeight * scale);

    wrapperRef.current.style.width = `${scaledWidth}px`;
    wrapperRef.current.style.height = `${scaledHeight}px`;
    wrapperRef.current.style.overflow = 'hidden';

    containerRef.current.style.transformOrigin = 'top left';
    containerRef.current.style.transform = `scale(${scale})`;
    containerRef.current.style.width = `${initialWidth}px`;
    containerRef.current.style.height = `${initialTotalHeight}px`;

    playerRef.current = player;
    isPlayerReadyRef.current = true;

    // Set up audio
    if (audioRef.current && audioUrl) {
      const audio = audioRef.current;
      audio.src = audioUrl;
      audio.load();
    }

    const syncAudioTime = (timeMs: number) => {
      if (!audioRef.current || !audioUrl) return;
      const nextTime = Math.max(0, timeMs / 1000);
      const currentTime = audioRef.current.currentTime;
      if (Math.abs(currentTime - nextTime) > audioSyncThresholdSec) {
        audioRef.current.currentTime = nextTime;
        lastAudioSyncRef.current = nextTime;
      }
    };

    player.addEventListener('ui-update-player-state', ({ payload }) => {
      if (!audioRef.current || !audioUrl) return;
      if (payload === 'playing') {
        audioRef.current.play().catch((err) => {
          console.warn('DomRecordingPlayer: audio playback failed', err);
        });
      }
      if (payload === 'paused') {
        audioRef.current.pause();
      }
    });

    player.addEventListener('ui-update-current-time', ({ payload }) => {
      if (typeof payload !== 'number') return;
      syncAudioTime(payload);
    });

    if (onPlayerReady) {
      onPlayerReady({
        goto: (timeInSeconds: number) => {
          if (audioRef.current && audioUrl) {
            audioRef.current.currentTime = Math.max(0, timeInSeconds);
          }
          player.goto(timeInSeconds * 1000);
        },
      });
    }

    return () => {
      isPlayerReadyRef.current = false;
      if (typeof (player as { destroy?: () => void }).destroy === 'function') {
        (player as { destroy: () => void }).destroy();
      }
      playerRef.current = null;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
    // Note: calculateDimensions intentionally excluded - it's only needed for initial sizing
    // All resize handling is done via CSS transforms in the separate resize effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, audioUrl, audioSyncThresholdSec, onPlayerReady]);

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-full text-muted-foreground ${className}`}>
        Loading DOM recording…
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center h-full text-muted-foreground ${className}`}>
        {error}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={className} style={{ overflow: 'hidden' }}>
      {audioUrl && <audio ref={audioRef} preload="metadata" />}
      <div ref={containerRef} />
    </div>
  );
};

export default DomRecordingPlayer;
